import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

// Theme = three independent axes applied as attributes on <html> (see
// index.css for the matching token blocks):
//   Feel    — data-feel (visual direction), Mode — .dark class,
//   Palette — data-palette (hue override; each feel has its own presets).
// index.html applies the persisted values pre-paint to avoid a theme flash.

export type Feel = 'mono' | 'quiet' | 'warm' | 'vivid';
export type Mode = 'light' | 'dark';

export interface PalettePreset {
  id: string;
  label: string;
  /** Swatch color for the picker dot. */
  swatch: string;
}

export const FEELS: Record<
  Feel,
  { label: string; blurb: string; dots: [string, string, string]; palettes: PalettePreset[] }
> = {
  mono: {
    label: 'Mono',
    blurb: 'Pure black & white',
    dots: ['oklch(0.2 0 0)', 'oklch(1 0 0)', 'oklch(0.18 0 0)'],
    palettes: [{ id: 'ink', label: 'Ink', swatch: 'oklch(0.2 0 0)' }],
  },
  quiet: {
    label: 'Quiet',
    blurb: 'Calm, restrained',
    dots: ['oklch(0.51 0.18 264)', 'oklch(0.93 0.006 262)', 'oklch(0.24 0.014 262)'],
    palettes: [
      { id: 'indigo', label: 'Indigo', swatch: 'oklch(0.51 0.18 264)' },
      { id: 'teal', label: 'Teal', swatch: 'oklch(0.51 0.18 205)' },
      { id: 'violet', label: 'Violet', swatch: 'oklch(0.51 0.18 300)' },
      { id: 'ember', label: 'Ember', swatch: 'oklch(0.55 0.15 45)' },
    ],
  },
  warm: {
    label: 'Warm',
    blurb: 'Ivory & clay, serif',
    dots: ['oklch(0.62 0.105 42)', 'oklch(0.94 0.012 85)', 'oklch(0.26 0.008 60)'],
    palettes: [
      { id: 'clay', label: 'Clay', swatch: 'oklch(0.62 0.105 42)' },
      { id: 'sage', label: 'Sage', swatch: 'oklch(0.62 0.105 152)' },
      { id: 'heather', label: 'Heather', swatch: 'oklch(0.62 0.105 310)' },
      { id: 'sea', label: 'Sea', swatch: 'oklch(0.62 0.105 235)' },
    ],
  },
  vivid: {
    label: 'Electric',
    blurb: 'Bold, gradient',
    dots: ['oklch(0.54 0.22 292)', 'oklch(0.93 0.06 220)', 'oklch(0.19 0.03 288)'],
    palettes: [
      { id: 'grape', label: 'Grape', swatch: 'oklch(0.54 0.22 292)' },
      { id: 'azure', label: 'Azure', swatch: 'oklch(0.54 0.2 255)' },
      { id: 'magenta', label: 'Magenta', swatch: 'oklch(0.54 0.22 325)' },
      { id: 'scarlet', label: 'Scarlet', swatch: 'oklch(0.55 0.2 20)' },
    ],
  },
};

const DEFAULT_FEEL: Feel = 'mono';
const KEYS = { feel: 'tf-feel', mode: 'tf-mode', palette: 'tf-palette' } as const;

function isFeel(v: string | null): v is Feel {
  return v === 'mono' || v === 'quiet' || v === 'warm' || v === 'vivid';
}

function initialFeel(): Feel {
  const v = localStorage.getItem(KEYS.feel);
  return isFeel(v) ? v : DEFAULT_FEEL;
}

function initialMode(): Mode {
  const v = localStorage.getItem(KEYS.mode);
  if (v === 'light' || v === 'dark') return v;
  return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function initialPalette(feel: Feel): string {
  const v = localStorage.getItem(KEYS.palette);
  if (v && FEELS[feel].palettes.some((p) => p.id === v)) return v;
  return FEELS[feel].palettes[0].id;
}

interface ThemeContextValue {
  feel: Feel;
  mode: Mode;
  palette: string;
  setFeel: (feel: Feel) => void;
  setMode: (mode: Mode) => void;
  setPalette: (palette: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [feel, setFeelState] = useState<Feel>(initialFeel);
  const [mode, setMode] = useState<Mode>(initialMode);
  const [palette, setPalette] = useState<string>(() => initialPalette(initialFeel()));

  // Feel change resets the palette to that feel's default.
  const setFeel = (next: Feel) => {
    setFeelState(next);
    setPalette(FEELS[next].palettes[0].id);
  };

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', mode === 'dark');
    root.dataset.feel = feel;
    root.dataset.palette = palette;
    localStorage.setItem(KEYS.feel, feel);
    localStorage.setItem(KEYS.mode, mode);
    localStorage.setItem(KEYS.palette, palette);
  }, [feel, mode, palette]);

  return (
    <ThemeContext.Provider value={{ feel, mode, palette, setFeel, setMode, setPalette }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
